// Contract overlay (ARCHITECTURE.md §8.4).
//
// Enriches code-level impact with contract-level information from the
// agent-contracts Navigation Index. Given the affected files and a
// file→artifact mapping, it determines which artifacts are implicated and, for
// each, the validations that must re-run, the agents that own it, its routes,
// and (separately) the workflows touched.
//
// The Navigation Index is accepted as a loose `Record<string, unknown>` so the
// analyzer stays decoupled from a specific agent-contracts version; readers are
// defensive and degrade to empty results when a field is absent. The expected
// shape is `ProjectNavigationIndex` (artifacts keyed by id, each carrying
// `agents.owners`, `operations.validators[].tool`, `files.path_patterns`, and
// optional `routes`).

import type { AffectedArtifact, ArtifactRoute } from "./types.js";

export interface ResolveAffectedArtifactsOptions {
  /** Affected file paths (from impact traversal, plus the changed files). */
  affectedFiles: string[];
  /** filePath → artifact IDs (typically `DependencyGraph.artifactMapping`). */
  artifactMapping: Map<string, string[]>;
  /** Optional agent-contracts Navigation Index for the contract overlay. */
  navigationIndex?: Record<string, unknown>;
}

// ---------------------------------------------------------------------------
// Defensive Navigation Index readers
// ---------------------------------------------------------------------------

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function asStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((v): v is string => typeof v === "string");
}

/** Look up the compiled artifact node for `artifactId`, if present. */
function artifactNode(
  navigationIndex: Record<string, unknown> | undefined,
  artifactId: string,
): Record<string, unknown> | undefined {
  if (!navigationIndex) return undefined;
  const artifacts = navigationIndex["artifacts"];
  if (!isRecord(artifacts)) return undefined;
  const node = artifacts[artifactId];
  return isRecord(node) ? node : undefined;
}

/**
 * Required validations for an artifact. Prefers an explicit
 * `required_validations` string array, falling back to the `tool` names of
 * `operations.validators` (the §8.4 "required_validations" overlay).
 */
function readRequiredValidations(node: Record<string, unknown>): string[] {
  const explicit = asStringArray(node["required_validations"]);
  if (explicit.length > 0) return explicit;

  const operations = node["operations"];
  if (isRecord(operations)) {
    const validators = operations["validators"];
    if (Array.isArray(validators)) {
      const tools = validators
        .map((v) => (isRecord(v) ? v["tool"] : undefined))
        .filter((t): t is string => typeof t === "string");
      return [...new Set(tools)];
    }
  }
  return [];
}

/** Owner agents for an artifact (`agents.owners`, or flat `ownerAgents`). */
function readOwnerAgents(node: Record<string, unknown>): string[] {
  const agents = node["agents"];
  if (isRecord(agents)) {
    const owners = asStringArray(agents["owners"]);
    if (owners.length > 0) return owners;
  }
  return asStringArray(node["ownerAgents"]);
}

/**
 * Routes for an artifact, expressed as the analyzer's `ArtifactRoute`
 * (artifactId → paths). Paths are the artifact's `files.path_patterns`; an
 * artifact with no patterns yields no route.
 */
function readRoutes(
  artifactId: string,
  node: Record<string, unknown>,
): ArtifactRoute[] {
  const files = node["files"];
  if (isRecord(files)) {
    const paths = asStringArray(files["path_patterns"]);
    if (paths.length > 0) return [{ artifactId, paths }];
  }
  return [];
}

/** Workflow ids declared on an artifact node, when present. */
function readArtifactWorkflows(node: Record<string, unknown>): string[] {
  return asStringArray(node["workflows"]);
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Map affected files to the artifacts they belong to and, via the Navigation
 * Index overlay, collect each artifact's required validations, owner agents,
 * and routes. Artifacts are returned in first-seen order; an artifact's
 * `affectedFiles` preserve the order in which they were encountered.
 */
export function resolveAffectedArtifacts(
  options: ResolveAffectedArtifactsOptions,
): AffectedArtifact[] {
  const { affectedFiles, artifactMapping, navigationIndex } = options;

  // Group affected files by artifact id, preserving discovery order.
  const filesByArtifact = new Map<string, string[]>();
  for (const file of affectedFiles) {
    for (const artifactId of artifactMapping.get(file) ?? []) {
      const list = filesByArtifact.get(artifactId);
      if (list) {
        if (!list.includes(file)) list.push(file);
      } else {
        filesByArtifact.set(artifactId, [file]);
      }
    }
  }

  const result: AffectedArtifact[] = [];
  for (const [artifactId, files] of filesByArtifact) {
    const node = artifactNode(navigationIndex, artifactId);
    result.push({
      artifactId,
      affectedFiles: files,
      requiredValidations: node ? readRequiredValidations(node) : [],
      ownerAgents: node ? readOwnerAgents(node) : [],
      routes: node ? readRoutes(artifactId, node) : [],
    });
  }

  return result;
}

/**
 * Determine which workflows are affected, based on the affected artifacts'
 * membership. A workflow is affected when it lists one of the affected
 * artifacts (`navigationIndex.workflows[id].artifacts`) or when an affected
 * artifact node declares it (`artifact.workflows`). Returns a de-duplicated,
 * stably-ordered list of workflow ids.
 */
export function resolveAffectedWorkflows(
  affectedArtifacts: AffectedArtifact[],
  navigationIndex?: Record<string, unknown>,
): string[] {
  const affectedIds = new Set(affectedArtifacts.map((a) => a.artifactId));
  const workflows: string[] = [];
  const add = (id: string): void => {
    if (!workflows.includes(id)) workflows.push(id);
  };

  // Source 1: per-artifact `workflows` declarations on the artifact nodes.
  for (const artifact of affectedArtifacts) {
    const node = artifactNode(navigationIndex, artifact.artifactId);
    if (node) {
      for (const wf of readArtifactWorkflows(node)) add(wf);
    }
  }

  // Source 2: a top-level `workflows` map whose entries reference artifacts.
  if (navigationIndex) {
    const wfMap = navigationIndex["workflows"];
    if (isRecord(wfMap)) {
      for (const [workflowId, def] of Object.entries(wfMap)) {
        if (!isRecord(def)) continue;
        const refs = asStringArray(def["artifacts"]);
        if (refs.some((id) => affectedIds.has(id))) add(workflowId);
      }
    }
  }

  return workflows;
}
